/**
 * React side of the focus model: every focusable item registers its handlers under its `group:index` key, shows
 * the focus rail when it is the focused item, and takes the focus when the mouse moves onto it (not when a scroll
 * slides it under a still mouse, `pointerMoved`). The App runs the pure focus model
 * (`nav.ts`) and calls the registered handlers on activate and adjust.
 */
import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react'
import { focusKey } from './nav'

export interface FocusHandlers {
  activate?: () => void
  adjust?: (delta: 1 | -1) => void
  /** Dashboard secondary action (F / pad X: Set course, Jump to, Track; spec §1.6). */
  secondary?: () => void
  /** Dashboard context action (R / pad Y: filters, sort; spec §1.6). */
  context?: () => void
}

export interface FocusApi {
  /** Key of the focused item. */
  focused: string
  register(key: string, handlers: FocusHandlers): () => void
  hover(group: string, index: number): void
}

export const FocusContext = createContext<FocusApi | null>(null)

export interface Focusable {
  key: string
  focused: boolean
  /** Spread onto the element: `data-nav`, `data-focused` and the hover handler. */
  props: { 'data-nav': string; 'data-focused'?: ''; onMouseEnter: (e: { clientX: number; clientY: number }) => void }
}

/** The client position of the last real mouse move (NaN before the first one). */
export interface PointerSpot {
  x: number
  y: number
}

/**
 * True when a `mouseenter` at (`x`, `y`) came from the mouse moving, false when the content moved under a still
 * mouse (a scroll, or a re-render that shifts rows). The browser sends `mouseenter` before the `mousemove` of the
 * same move, so a real move enters at a new spot, while a scroll or a layout shift enters at the last move's spot.
 * Without this, scrolling towards an offer (the wheel, a keyboard move, or a click scrolling its button into view)
 * let the slot row that slid under the cursor take the focus and swap the offers away (the station-ui
 * salvaged-reactor e2e: the Power plant offers turned into Weapon 2's mid-click).
 */
export function pointerMoved(last: PointerSpot, x: number, y: number): boolean {
  return x !== last.x || y !== last.y
}

const lastMove: PointerSpot = { x: Number.NaN, y: Number.NaN }
let tracking = false

/** Starts recording the last real mouse move (once, window-wide; UI events only). */
function trackPointer(): void {
  if (tracking || typeof window === 'undefined') return
  tracking = true
  window.addEventListener(
    'mousemove',
    (e) => {
      lastMove.x = e.clientX
      lastMove.y = e.clientY
    },
    { capture: true, passive: true },
  )
}

/** Registers `handlers` for the item at `group:index` (the latest handlers are always the ones called). */
export function useFocusable(group: string, index: number, handlers: FocusHandlers): Focusable {
  const api = useContext(FocusContext)
  const key = focusKey(group, index)
  const latest = useRef(handlers)
  useLayoutEffect(() => {
    latest.current = handlers
  })
  useLayoutEffect(trackPointer, [])
  useLayoutEffect(() => {
    if (api === null) return undefined
    return api.register(key, {
      activate: () => latest.current.activate?.(),
      adjust: (d) => latest.current.adjust?.(d),
      secondary: () => latest.current.secondary?.(),
      context: () => latest.current.context?.(),
    })
  }, [api, key])
  const focused = api !== null && api.focused === key
  return {
    key,
    focused,
    props: {
      'data-nav': key,
      ...(focused ? { 'data-focused': '' as const } : {}),
      onMouseEnter: (e) => {
        if (pointerMoved(lastMove, e.clientX, e.clientY)) api?.hover(group, index)
      },
    },
  }
}

export interface FocusButtonProps {
  group: string
  index: number
  onActivate: () => void
  disabled?: boolean
  testId?: string
  className?: string
  title?: string
  /** Stepper items: left/right on the keyboard or pad. */
  onAdjust?: (delta: 1 | -1) => void
  children: ReactNode
}

/**
 * Pointer = focus (H9, dashboard spec §5.7): the click handler moves the focus model's cursor to the clicked item,
 * then activates it, so the keyboard marker follows the mouse (it stayed on MARKET while a clicked tab was active).
 */
export function focusOnClick(api: Pick<FocusApi, 'hover'> | null, group: string, index: number, activate: () => void): () => void {
  return () => {
    api?.hover(group, index)
    activate()
  }
}

/** A focusable button: click, Enter/Space/A (through the focus model) and hover all work the same. */
export function FocusButton(p: FocusButtonProps): ReactNode {
  const api = useContext(FocusContext)
  const f = useFocusable(p.group, p.index, {
    activate: p.disabled === true ? undefined : p.onActivate,
    adjust: p.onAdjust,
  })
  return (
    <button
      type="button"
      tabIndex={-1}
      className={`ui-btn${p.className === undefined ? '' : ` ${p.className}`}`}
      data-testid={p.testId}
      title={p.title}
      disabled={p.disabled === true}
      onClick={focusOnClick(api, p.group, p.index, p.onActivate)}
      {...f.props}
    >
      {p.children}
    </button>
  )
}

/** A −/+ stepper for mouse users (the focused row also steps with left/right). */
export function Stepper(p: { value: string; onStep: (delta: 1 | -1) => void; disabled?: boolean; label: string }): ReactNode {
  return (
    <span className="ui-stepper" aria-label={p.label}>
      <button type="button" tabIndex={-1} className="ui-stepper__btn" disabled={p.disabled === true} onClick={() => p.onStep(-1)} aria-label={`Less ${p.label}`}>
        {'−'}
      </button>
      <span className="ui-stepper__value">{p.value}</span>
      <button type="button" tabIndex={-1} className="ui-stepper__btn" disabled={p.disabled === true} onClick={() => p.onStep(1)} aria-label={`More ${p.label}`}>
        +
      </button>
    </span>
  )
}

/** A thin 0..1 meter with a text label beside it (never colour alone). */
export function Meter(p: { value: number; label: string; tone?: 'ok' | 'warning' | 'danger' }): ReactNode {
  const v = Math.max(0, Math.min(1, p.value))
  return (
    <span className="ui-meter" data-tone={p.tone ?? 'ok'}>
      <span className="ui-meter__bar">
        <span className="ui-meter__fill" style={{ width: `${(v * 100).toFixed(1)}%` }} />
      </span>
      <span className="ui-meter__label">{p.label}</span>
    </span>
  )
}
